import React, { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { IconActivityHeartbeat, IconLoader2, IconUser } from '@tabler/icons-react';
import { pb } from '../../../shared/api/pocketbase';
import { ConfirmModal } from '../../../shared/ui/ConfirmModal';
import type { Tag } from '../../../entities/tag';

import { CardModalProps } from '../model/cardModalTypes';
import { useCardModalAssignments } from '../model/useCardModalAssignments';
import { useCardModalFiles } from '../model/useCardModalFiles';
import { useCardModalLeadForm } from '../model/useCardModalLeadForm';
import { CardDocReadiness } from './CardDocReadiness';
import { CardModalFeedPane } from './CardModalFeedPane';
import { CardModalHeader } from './CardModalHeader';
import { CardModalInfoPane } from './CardModalInfoPane';

const CardModal: React.FC<CardModalProps> = ({
  lead,
  stages,
  isAdmin,
  history,
  actions,
  smsLogs,
  isOpen,
  notFound,
  onClose,
  onSave,
  onDelete,
  onAddComment,
  onActivityChanged,
  onNotify,
}) => {
  const leadForm = useCardModalLeadForm(lead, onSave);
  const files = useCardModalFiles({
    isOpen,
    leadId: lead?.id ?? null,
    onActivityChanged,
    onNotify,
  });
  const assignments = useCardModalAssignments({
    lead,
    isOpen,
    onNotify,
  });

  const [comment, setComment] = useState('');
  const [copiedLeadId, setCopiedLeadId] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [allTags, setAllTags] = useState<Tag[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    pb.collection('tags')
      .getFullList<Tag>({ sort: 'name', fields: 'id,name,colors' })
      .then(setAllTags)
      .catch(() => setAllTags([]));
  }, [isOpen]);
  const [mobileTab, setMobileTab] = useState<'info' | 'feed'>('info');
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDataLoading = files.isFilesLoading || assignments.isAssignmentLoading;
  const isContentLoading = isDataLoading || !leadForm.isReady;

  useEffect(() => {
    setComment('');
    setCopiedLeadId(false);
    setConfirmDelete(false);
  }, [lead?.id]);

  useEffect(() => {
    if (!isContentLoading && isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isContentLoading, isOpen]);

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, isOpen]);

  useEffect(() => {
    if (mobileTab === 'feed' && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [mobileTab]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleAddComment = useCallback(() => {
    const text = comment.trim();
    if (!text) {
      return;
    }
    onAddComment(text);
    setComment('');
  }, [comment, onAddComment]);

if (!isOpen) return null;

  if (notFound) {
    return (
      <div className="mo open" onClick={onClose}>
        <div className="cm cm-card cm-not-found" onClick={e => e.stopPropagation()}>
          <div className="cm-not-found-body">
            <span className="cm-not-found-icon">🔒</span>
            <p className="cm-not-found-title">Нет доступа к карточке</p>
            <p className="cm-not-found-sub">Карточка не найдена или у вас нет прав для её просмотра.</p>
            <button className="btn-can" type="button" onClick={onClose}>Закрыть</button>
          </div>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="mo open" onClick={handleOverlayClick}>
      <div className="cm cm-card">
        <CardModalHeader
          lead={lead}
          stages={stages}
          boardId={leadForm.watch('boardId') ?? ''}
          copiedLeadId={copiedLeadId}
          leadSource={lead.source}
          allTags={allTags}
          tagValues={leadForm.watch('tags') ?? []}
          onTagsChange={v => leadForm.setValue('tags', v, { shouldDirty: true })}
          onBoardIdChange={v => leadForm.setValue('boardId', v, { shouldDirty: true })}
          onCopyLeadId={() => {
            navigator.clipboard.writeText(lead.id).then(() => {
              setCopiedLeadId(true);
              window.setTimeout(() => setCopiedLeadId(false), 1500);
            }).catch(() => {});
          }}
          onClose={onClose}
        />

        {!isContentLoading && <CardDocReadiness files={files} />}

        <div className="cm-body">
          <div className="cm-mobile-tabs">
            <button
              className={`cm-mobile-tab${mobileTab === 'info' ? ' active' : ''}`}
              onClick={() => setMobileTab('info')}
              type="button"
            >
              <IconUser size={15} stroke={2} />
              Информация
            </button>
            <button
              className={`cm-mobile-tab${mobileTab === 'feed' ? ' active' : ''}`}
              onClick={() => setMobileTab('feed')}
              type="button"
            >
              <IconActivityHeartbeat size={15} stroke={2} />
              Лента
            </button>
          </div>

          {isContentLoading && (
            <div className="cm-loading">
              <IconLoader2 size={32} stroke={2} className="spin" />
            </div>
          )}

          {!isContentLoading && (
            <>
              <CardModalInfoPane
                isAdmin={isAdmin}
                hidden={mobileTab !== 'info'}
                loading={false}
                leadId={lead.id}
                leadOwnerId={lead.userId}
                control={leadForm.control}
                register={leadForm.register}
                errors={leadForm.errors}
                getValues={leadForm.getValues}
                setValue={leadForm.setValue}
                files={files}
                onClose={onClose}
                onSave={leadForm.submit}
                onDelete={onDelete ? () => setConfirmDelete(true) : undefined}
                onNotify={onNotify}
              />

              <CardModalFeedPane
                hidden={mobileTab !== 'feed'}
                loading={false}
                history={history}
                actions={actions}
                smsLogs={smsLogs}
                assignments={assignments}
                pinPrefill={{
                  fullName: lead?.title ?? '',
                  phone: lead?.phone ?? '',
                  departureCity: lead?.city ?? '',
                  desiredDate: /^\d{4}-\d{2}-\d{2}/.test(lead?.arrival ?? '') ? lead!.arrival!.slice(0, 10) : '',
                }}
                comment={comment}
                onCommentChange={setComment}
                onAddComment={handleAddComment}
                scrollRef={scrollRef}
              />
            </>
          )}
        </div>
      </div>

      {confirmDelete && onDelete && (
        <ConfirmModal
          title="Удалить карточку?"
          content={`Карточка «${lead.title?.trim() || 'Без названия'}» будет удалена безвозвратно.`}
          onConfirm={() => { setConfirmDelete(false); onDelete(lead); }}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
};

export { CardModal };
